import React, { useState, useEffect } from 'react';
import { Card, Alert } from 'react-bootstrap';
import InputField from '../../../components/InputField';
import TextAreaField from '../../../components/TextAreaField';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import FileUpload from '../../../components/FileUpload';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import useDefinitiveResolutionService from '../../../app/services/hooks/useDefinitiveResolutionService';
import useHolidayService from '../../../app/services/hooks/useHolidayService';
import useCurrencyService from '../../../app/services/hooks/useCurrencyService';
import usePermissions from '../../../hooks/usePermissions';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { safeRedirectUrl } from '../../Organization/types';

interface DefinitiveResolutionStepProps {
    brandRegistrationId: number;
    onUpdate: () => void;
    isEditable: boolean;
    paymentManagedByFirm?: boolean;  // Caso de un estudio: el pago lo acuerda el estudio con su cliente
}

const DefinitiveResolutionStep: React.FC<DefinitiveResolutionStepProps> = ({
    brandRegistrationId,
    onUpdate,
    isEditable,
    paymentManagedByFirm = false
}) => {
    const { can } = usePermissions();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { requestConfirmation, successAlert } = useReactConfirmAlert();
    const {
        getSubstantiveExaminationId,
        getDefinitiveResolution,
        createDefinitiveResolution,
        updateDefinitiveResolution,
        sendDefinitiveResolutionPaymentEmail,
        createFlowPaymentForDefinitiveResolution,
        markAsPaidDefinitiveResolution,
        fetchingGetSubstantiveExaminationId,
        fetchingGetDefinitiveResolution,
        fetchingCreateDefinitiveResolution,
        fetchingUpdateDefinitiveResolution,
        fetchingSendPaymentEmail,
        fetchingCreateFlowPayment,
        fetchingMarkAsPaid
    } = useDefinitiveResolutionService();

    const [substantiveExaminationId, setSubstantiveExaminationId] = useState<number | null>(null);
    const [resolutionData, setResolutionData] = useState<any>(null);
    const [form, setForm] = useState({
        definitive_rights_fee: '',
        resolution_notification_date: '',
        days_remaining: '60', // Por defecto 60 días hábiles
        observations: '',
        internal_payment_agreement: false
    });
    const [certificateFile, setCertificateFile] = useState<File | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [paymentDeadlineDate, setPaymentDeadlineDate] = useState<string>('');
    const [utmValue, setUtmValue] = useState<number>(0);
    const [isEditing, setIsEditing] = useState(false);
    
    // Hook para calcular fechas con días hábiles
    const { calculateBusinessDate, fetchingCalculateBusinessDate } = useHolidayService();
    
    // Hook para obtener valor de UTM
    const { getCurrentCurrencyValue } = useCurrencyService();
    
    // Verificar si el usuario es abogado o admin
    const isLawyer = can('change_brandregistrationinapiapplication');

    useEffect(() => {
        if (brandRegistrationId) {
            getSubstantiveExaminationId(brandRegistrationId, {
                onSuccess: (response: ServiceResponse) => {
                    if (response.data && response.data.substantive_examination_id) {
                        const fetchedExamId = response.data.substantive_examination_id;
                        setSubstantiveExaminationId(fetchedExamId);
                        loadExistingData(fetchedExamId);
                    } else {
                        // No hay substantive_examination_id
                        if (isLawyer) {
                            setShowCreateForm(true);
                        }
                    }
                },
                onError: (response: ServiceResponse) => {
                    // Error al obtener el ID
                    console.log('Error obteniendo substantive_examination_id:', response);
                    if (isLawyer) {
                        setShowCreateForm(true);
                    }
                }
            });
            
            // Cargar valor de UTM
            loadUTMValue();
        }
    }, [brandRegistrationId]);
    
    // Función para cargar valor de UTM
    const loadUTMValue = () => {
        getCurrentCurrencyValue('UTM', {
            onSuccess: (response: ServiceResponse) => {
                if (response.data && response.data.exchange) {
                    setUtmValue(response.data.exchange.value);
                }
            },
            onError: () => {
                console.log('No se pudo cargar el valor de UTM');
            }
        });
    };

    // Calcular fecha límite de pago cuando cambien la fecha de notificación o los días
    useEffect(() => {
        if (form.resolution_notification_date && form.days_remaining) {
            const days = parseInt(form.days_remaining, 10);
            if (!isNaN(days) && days > 0) {
                calculateBusinessDate(form.resolution_notification_date, days, {
                    onSuccess: (res) => {
                        setPaymentDeadlineDate(res.data.end_date_formatted);
                    },
                    onError: () => {
                        setPaymentDeadlineDate('');
                    }
                });
            }
        } else {
            setPaymentDeadlineDate('');
        }
    }, [form.resolution_notification_date, form.days_remaining]);

    // Función para recalcular manualmente la fecha límite
    const handleRecalculateDeadline = () => {
        if (form.resolution_notification_date && form.days_remaining) {
            const days = parseInt(form.days_remaining, 10);
            if (!isNaN(days) && days > 0) {
                calculateBusinessDate(form.resolution_notification_date, days, {
                    onSuccess: (res) => {
                        setPaymentDeadlineDate(res.data.end_date_formatted);
                        toast.success('Fecha límite recalculada correctamente');
                    },
                    onError: () => {
                        setPaymentDeadlineDate('');
                        toast.error('Error al calcular la fecha');
                    }
                });
            }
        }
    };

    const loadExistingData = (examId: number) => {
        getDefinitiveResolution(examId, {
            onSuccess: (res) => {
                const resolution = res.data.definitive_resolution;
                setResolutionData(resolution);
                setForm({
                    definitive_rights_fee: resolution.definitive_rights_fee || '',
                    resolution_notification_date: resolution.resolution_notification_date || '',
                    days_remaining: resolution.days_remaining || '60', // Por defecto 60 días hábiles
                    observations: resolution.observations || '',
                    internal_payment_agreement: resolution.internal_payment_agreement || false
                });
                setShowCreateForm(false);
            },
            onError: () => {
                setShowCreateForm(true);
            }
        });
    };

    const handleInputChange = (field: string, value: string | boolean) => {
        setForm({ ...form, [field]: value });
    };

    const handleCreateResolution = () => {
        if (!substantiveExaminationId) {
            toast.error('No se encontró el examen de fondo');
            return;
        }

        const data = {
            substantive_examination_id: substantiveExaminationId,
            ...form
        };

        createDefinitiveResolution(data, {
            onSuccess: (res) => {
                toast.success(res.message);
                setErrors({});
                if (substantiveExaminationId) {
                    loadExistingData(substantiveExaminationId);
                }
            },
            onError: (res) => {
                toast.error(res.message);
            },
            onFieldError: (res) => {
                setErrors(res.data);
                toast.error('Por favor corrige los errores en el formulario');
            }
        });
    };

    const handleSendPaymentEmail = () => {
        if (!resolutionData?.id) return;

        requestConfirmation({
            title: '¿Enviar solicitud de pago?',
            message: 'Se enviará un correo al solicitante para realizar el pago de los derechos definitivos.',
            buttons: {
                confirmButton: {
                    label: 'Sí, enviar',
                    onClick: () => {
                        sendDefinitiveResolutionPaymentEmail(resolutionData.id, {
                            onSuccess: (res) => {
                                successAlert({
                                    title: 'Correo enviado',
                                    message: 'La solicitud de pago ha sido enviada exitosamente al solicitante.'
                                });
                            },
                            onError: (res) => {
                                toast.error(res.message);
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => {
                        // Modal se cierra automáticamente
                    }
                }
            }
        });
    };

    const handlePayNow = () => {
        if (!resolutionData?.id) return;

        createFlowPaymentForDefinitiveResolution(resolutionData.id, {
            onSuccess: (res) => {
                window.location.href = safeRedirectUrl(res.data.payment_url);
            },
            onError: (res) => {
                toast.error(res.message);
            }
        });
    };

    const handleMarkAsPaid = () => {
        if (!resolutionData?.id) return;

        requestConfirmation({
            title: '¿Marcar como pagado?',
            message: 'Esta acción marcará la resolución definitiva como pagada. Esta acción no se puede deshacer.',
            buttons: {
                confirmButton: {
                    label: 'Sí, marcar como pagado',
                    onClick: () => {
                        markAsPaidDefinitiveResolution(resolutionData.id, {
                            onSuccess: (res) => {
                                successAlert({
                                    title: 'Pago registrado',
                                    message: 'La resolución definitiva ha sido marcada como pagada exitosamente.'
                                });
                                if (substantiveExaminationId) {
                                    loadExistingData(substantiveExaminationId);
                                }
                                onUpdate();
                            },
                            onError: (res) => {
                                toast.error(res.message);
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    const handleUpdateResolution = (closeEditingOnSuccess = false) => {
        if (!resolutionData?.id) return;

        // Pasar objeto simple, useFetch lo convierte a FormData automáticamente
        const dataToSend: Record<string, any> = { ...form };
        
        if (certificateFile) {
            dataToSend.certificate_file = certificateFile;
        }

        updateDefinitiveResolution(resolutionData.id, dataToSend, {
            onSuccess: (res) => {
                toast.success(res.message);
                setErrors({});
                if (substantiveExaminationId) {
                    loadExistingData(substantiveExaminationId);
                }
                if (closeEditingOnSuccess) {
                    setIsEditing(false);
                }
                onUpdate();
            },
            onError: (res) => {
                toast.error(res.message);
            },
            onFieldError: (res) => {
                setErrors(res.data);
                toast.error('Por favor corrige los errores en el formulario');
            }
        });
    };

    const formatUTM = (value: number) => {
        return `${value} UTM`;
    };
    
    const formatCLP = (value: number) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(value);
    };
    
    // Calcula el equivalente en CLP de un monto en UTM
    const utmToCLP = (utmAmount: number): number => {
        return Math.round(utmAmount * utmValue);
    };

    // Convierte fecha de formato "dd/mm/yyyy" a "yyyy-mm-dd" para input type="date"
    const convertFormattedDateToISO = (formattedDate: string): string => {
        const parts = formattedDate.split('/');
        if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return '';
    };

    // Loading
    if (fetchingGetSubstantiveExaminationId || fetchingGetDefinitiveResolution) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    // Si no tenemos substantive_examination_id, mostrar mensaje apropiado según el rol
    if (!substantiveExaminationId) {
        return (
            <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                {isLawyer ? 
                    'No se encontró el examen de fondo. Por favor asegúrate de haber completado la etapa 6.' :
                    'Esta etapa aún no está disponible. Su abogado debe completar primero la etapa 6 (Examen de fondo).'}
            </div>
        );
    }

    // Si no hay resolución y el usuario es abogado, mostrar formulario de creación
    if (showCreateForm && isLawyer) {
        return (
            <div className="step-container">
                <div className="step-header mb-4">
                    <h4 className="">
                        <i className="fas fa-gavel me-2 text-success"></i>
                        Crear Resolución Definitiva
                    </h4>
                    <p className="text-muted mb-0">Configure la resolución para solicitar el pago de derechos definitivos al cliente.</p>
                </div>

                <Alert variant="info">
                    <h6>La solicitud fue ingresada a INAPI exitosamente.</h6>
                    <p className="mb-0">
                        Para continuar con el proceso <strong>se debe realizar el pago de los derechos definitivos</strong>, 
                        lo cual requiere pago.
                    </p>
                </Alert>

                <div className="row g-3 mt-3">
                    <div className="col-md-12">
                        <InputField
                            label="Tarifa correspondiente a las clases finales (UTM)"
                            type="number"
                            value={form.definitive_rights_fee}
                            onChange={(value: string) => handleInputChange('definitive_rights_fee', value)}
                            onFocus={() => onFocusRemove('definitive_rights_fee')}
                            placeholder="2.5"
                            error={fieldErrorMessage('definitive_rights_fee')}
                        />
                        <small className="text-muted">
                            Ingrese la tarifa en UTM que el cliente debe pagar por los derechos definitivos.
                        </small>
                    </div>
                </div>

                {/* Checkbox para pago acordado internamente (en casos de estudio siempre lo es) */}
                {paymentManagedByFirm ? (
                    <Alert variant="info" className="mt-4 mb-0" data-testid="payment-managed-by-firm">
                        <i className="fas fa-handshake me-2"></i>
                        Pago acordado internamente con el cliente.
                    </Alert>
                ) : (
                    <div 
                        className={`p-4 rounded-3 mt-4 ${form.internal_payment_agreement ? 'border border-2' : 'border'}`}
                        style={{
                            backgroundColor: form.internal_payment_agreement ? '#fff8e1' : '#f8f9fa',
                            borderColor: form.internal_payment_agreement ? '#ffc107' : '#dee2e6',
                            transition: 'all 0.3s ease',
                            boxShadow: form.internal_payment_agreement ? '0 4px 12px rgba(255, 193, 7, 0.15)' : 'none'
                        }}
                    >
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="internal_payment_agreement_create"
                                checked={form.internal_payment_agreement || false}
                                onChange={(e) => handleInputChange('internal_payment_agreement', e.target.checked)}
                                style={{
                                    width: '1.3em',
                                    height: '1.3em',
                                    marginTop: '0.15em',
                                    cursor: 'pointer'
                                }}
                            />
                            <label 
                                className="form-check-label ms-2" 
                                htmlFor="internal_payment_agreement_create"
                                style={{ cursor: 'pointer' }}
                            >
                                <span 
                                    className="d-flex align-items-center gap-2"
                                    style={{
                                        fontSize: '1rem',
                                        fontWeight: form.internal_payment_agreement ? 600 : 500,
                                        color: form.internal_payment_agreement ? '#001849' : '#495057'
                                    }}
                                >
                                    <i 
                                        className="fas fa-handshake"
                                        style={{
                                            color: form.internal_payment_agreement ? '#ffc107' : '#6c757d'
                                        }}
                                    ></i>
                                    Pago acordado internamente
                                </span>
                                {form.internal_payment_agreement && (
                                    <small className="text-muted d-block mt-1">
                                        El pago fue acordado directamente con el cliente (transferencia bancaria, efectivo, etc.). 
                                        No se enviará link de pago, pero el abogado podrá marcarlo como pagado manualmente.
                                    </small>
                                )}
                            </label>
                        </div>
                    </div>
                )}

                <Alert variant="warning" className="mt-3">
                    <strong>IMPORTANTE:</strong>
                    <p className="mb-0">
                        El pago se debe realizar dentro de los próximos 60 días, emitida la notificación de resolución.
                    </p>
                </Alert>

                <div className="d-flex justify-content-end mt-4">
                    <ButtonGeneral
                        type="button"
                        variant="primary"
                        size="medium"
                        title={fetchingCreateDefinitiveResolution ? 'Creando...' : 'Crear Resolución'}
                        onClick={handleCreateResolution}
                        loading={fetchingCreateDefinitiveResolution}
                        disabled={fetchingCreateDefinitiveResolution}
                        icon="fas fa-plus"
                        iconPosition="left"
                    />
                </div>
            </div>
        );
    }

    // Si no hay resolución y no es abogado
    if (!resolutionData) {
        return (
            <div className="step-container">
                <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    La resolución definitiva aún no ha sido configurada. Por favor espere a que su abogado configure esta etapa.
                </div>
            </div>
        );
    }

    // Mostrar información de la resolución
    return (
        <div className="step-container">
            <div className="step-header mb-4">
                <h4 className="">
                    <i className="fas fa-gavel me-2 text-success"></i>
                    Resolución Definitiva
                </h4>
                <p className="text-muted mb-0">Gestión del pago de derechos definitivos y certificado de marca.</p>
            </div>

            {/* Información de pago */}
            <div className="card mb-4">
                <div className="card-header bg-light">
                    <h5 className="mb-0">
                        <i className="fas fa-dollar-sign me-2"></i>
                        Información de Pago
                    </h5>
                </div>
                <div className="card-body">
                    <Alert variant="info">
                        <h6>La solicitud fue ingresada a INAPI exitosamente.</h6>
                        <p className="mb-0">
                            Para continuar con el proceso <strong>se debe realizar el pago de los derechos definitivos</strong>, 
                            lo cual requiere pago.
                        </p>
                    </Alert>

                    <div className="row mt-3">
                        <div className="col-md-6">
                            <p className="mb-2">
                                <strong>Tarifa:</strong> {formatUTM(Number(resolutionData.definitive_rights_fee))}
                                {utmValue > 0 && (
                                    <span className="text-muted ms-2">
                                        ({formatCLP(utmToCLP(Number(resolutionData.definitive_rights_fee)))})
                                    </span>
                                )}
                            </p>
                            <p className="mb-2">
                                <strong>Estado:</strong>{' '}
                                <span className={`badge bg-${resolutionData.payment_status === 'paid' ? 'success' : 'warning'}`}>
                                    {resolutionData.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                                </span>
                            </p>
                            {resolutionData.internal_payment_agreement && (
                                <p className="mb-2">
                                    <span className="badge bg-info">
                                        <i className="fas fa-handshake me-1"></i>
                                        Pago acordado internamente
                                    </span>
                                </p>
                            )}
                        </div>
                        <div className="col-md-6">
                            {resolutionData.payment_date && (
                                <p className="mb-2">
                                    <strong>Fecha de pago:</strong> {new Date(resolutionData.payment_date).toLocaleDateString('es-CL')}
                                </p>
                            )}
                        </div>
                    </div>

                    <Alert variant="warning" className="mt-3">
                        <strong>IMPORTANTE:</strong>
                        <p className="mb-0">
                            El pago se debe realizar dentro de los próximos <strong>60 días</strong>, 
                            emitida la notificación de resolución.
                        </p>
                    </Alert>

                    <div className="mt-3 d-flex gap-2 flex-wrap">
                        {/* Botón editar (solo para abogado y si no está pagado) */}
                        {isLawyer && resolutionData.payment_status !== 'paid' && (
                            <ButtonGeneral
                                type="button"
                                variant="outline"
                                size="small"
                                title="Editar"
                                onClick={() => setIsEditing(true)}
                                icon="fas fa-edit"
                                iconPosition="left"
                            />
                        )}

                        {/* Botón enviar solicitud de pago (solo si NO es pago interno) */}
                        {isLawyer && !paymentManagedByFirm && resolutionData.payment_status !== 'paid' && !resolutionData.internal_payment_agreement && (
                            <ButtonGeneral
                                type="button"
                                variant="secondary"
                                size="small"
                                title={fetchingSendPaymentEmail ? 'Enviando...' : 'Enviar solicitud de pago'}
                                onClick={handleSendPaymentEmail}
                                loading={fetchingSendPaymentEmail}
                                disabled={fetchingSendPaymentEmail}
                                icon="fas fa-envelope"
                                iconPosition="left"
                            />
                        )}

                        {/* Botón marcar como pagado (solo si ES pago interno y es abogado) */}
                        {isLawyer && resolutionData.payment_status !== 'paid' && resolutionData.internal_payment_agreement && (
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                size="small"
                                title={fetchingMarkAsPaid ? 'Procesando...' : 'Marcar como pagado'}
                                onClick={handleMarkAsPaid}
                                loading={fetchingMarkAsPaid}
                                disabled={fetchingMarkAsPaid}
                                icon="fas fa-check-circle"
                                iconPosition="left"
                            />
                        )}

                        {/* Botón pagar ahora (solo para cliente y si NO es pago interno) */}
                        {!isLawyer && !paymentManagedByFirm && resolutionData.payment_status !== 'paid' && !resolutionData.internal_payment_agreement && (
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                size="medium"
                                title={fetchingCreateFlowPayment ? 'Procesando...' : 'Pagar Ahora'}
                                onClick={handlePayNow}
                                loading={fetchingCreateFlowPayment}
                                disabled={fetchingCreateFlowPayment}
                                icon="fas fa-credit-card"
                                iconPosition="left"
                            />
                        )}

                        {/* Mensaje para cliente cuando es pago interno */}
                        {!isLawyer && paymentManagedByFirm && resolutionData.payment_status !== 'paid' && (
                            <Alert variant="info" className="mb-0 w-100" data-testid="payment-managed-by-firm">
                                <i className="fas fa-handshake me-2"></i>
                                El pago de este trámite lo acuerdas directamente con tu estudio.
                            </Alert>
                        )}
                        {isLawyer && paymentManagedByFirm && resolutionData.payment_status !== 'paid' && (
                            <small className="text-muted align-self-center">Pago acordado internamente con el cliente.</small>
                        )}

                        {/* Mensaje para cliente cuando es pago interno */}
                        {!isLawyer && !paymentManagedByFirm && resolutionData.payment_status !== 'paid' && resolutionData.internal_payment_agreement && (
                            <Alert variant="info" className="mb-0 w-100">
                                <i className="fas fa-handshake me-2"></i>
                                <strong>Pago acordado internamente:</strong> El abogado y el cliente acordaron el pago sin pasar por la pasarela. 
                                Espere a que el abogado confirme la recepción del pago.
                            </Alert>
                        )}
                    </div>
                </div>
            </div>

            {/* Formulario de edición (para abogado cuando no está pagado) */}
            {isEditing && isLawyer && resolutionData.payment_status !== 'paid' && (
                <div className="card mb-4">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fas fa-edit me-2"></i>
                            Editar Información de Pago
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={() => setIsEditing(false)}
                            aria-label="Cerrar"
                        ></button>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-12">
                                <InputField
                                    label="Tarifa correspondiente a las clases finales (UTM)"
                                    type="number"
                                    value={form.definitive_rights_fee}
                                    onChange={(value: string) => handleInputChange('definitive_rights_fee', value)}
                                    onFocus={() => onFocusRemove('definitive_rights_fee')}
                                    placeholder="2.5"
                                    error={fieldErrorMessage('definitive_rights_fee')}
                                />
                                {utmValue > 0 && form.definitive_rights_fee && (
                                    <small className="text-muted">
                                        Equivalente: {formatCLP(utmToCLP(Number(form.definitive_rights_fee)))}
                                    </small>
                                )}
                            </div>
                        </div>

                        {/* En casos de estudio el pago siempre se acuerda internamente: no se edita */}
                        {!paymentManagedByFirm && (
                            <div 
                                className={`p-4 rounded-3 mt-4 ${form.internal_payment_agreement ? 'border border-2' : 'border'}`}
                                style={{
                                    backgroundColor: form.internal_payment_agreement ? '#fff8e1' : '#f8f9fa',
                                    borderColor: form.internal_payment_agreement ? '#ffc107' : '#dee2e6',
                                    transition: 'all 0.3s ease',
                                    boxShadow: form.internal_payment_agreement ? '0 4px 12px rgba(255, 193, 7, 0.15)' : 'none'
                                }}
                            >
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="internal_payment_agreement_edit"
                                        checked={form.internal_payment_agreement || false}
                                        onChange={(e) => handleInputChange('internal_payment_agreement', e.target.checked)}
                                        style={{
                                            width: '1.3em',
                                            height: '1.3em',
                                            marginTop: '0.15em',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <label 
                                        className="form-check-label ms-2" 
                                        htmlFor="internal_payment_agreement_edit"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span 
                                            className="d-flex align-items-center gap-2"
                                            style={{
                                                fontSize: '1rem',
                                                fontWeight: form.internal_payment_agreement ? 600 : 500,
                                                color: form.internal_payment_agreement ? '#001849' : '#495057'
                                            }}
                                        >
                                            <i 
                                                className="fas fa-handshake"
                                                style={{
                                                    color: form.internal_payment_agreement ? '#ffc107' : '#6c757d'
                                                }}
                                            ></i>
                                            Pago acordado internamente
                                        </span>
                                        {form.internal_payment_agreement && (
                                            <small className="text-muted d-block mt-1">
                                                El pago fue acordado directamente con el cliente (transferencia bancaria, efectivo, etc.). 
                                                No se enviará link de pago, pero podrás marcarlo como pagado manualmente.
                                            </small>
                                        )}
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <ButtonGeneral
                                type="button"
                                variant="secondary"
                                size="medium"
                                title="Cancelar"
                                onClick={() => {
                                    setIsEditing(false);
                                    // Restaurar valores originales
                                    if (resolutionData) {
                                        setForm({
                                            definitive_rights_fee: resolutionData.definitive_rights_fee || '',
                                            resolution_notification_date: resolutionData.resolution_notification_date || '',
                                            days_remaining: resolutionData.days_remaining || '60',
                                            observations: resolutionData.observations || '',
                                            internal_payment_agreement: resolutionData.internal_payment_agreement || false
                                        });
                                    }
                                }}
                            />
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                size="medium"
                                title={fetchingUpdateDefinitiveResolution ? 'Guardando...' : 'Guardar cambios'}
                                onClick={() => handleUpdateResolution(true)}
                                loading={fetchingUpdateDefinitiveResolution}
                                disabled={fetchingUpdateDefinitiveResolution}
                                icon="fas fa-save"
                                iconPosition="left"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Formulario de actualización (solo si está pagado) */}
            {resolutionData.payment_status === 'paid' && isEditable && (
                <div className="card">
                    <div className="card-header bg-light">
                        <h5 className="mb-0">
                            <i className="fas fa-edit me-2"></i>
                            Información Adicional
                        </h5>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-4">
                                <InputField
                                    label="Fecha de notificación de resolución"
                                    type="date"
                                    value={form.resolution_notification_date}
                                    onChange={(value) => handleInputChange('resolution_notification_date', value)}
                                    onFocus={() => onFocusRemove('resolution_notification_date')}
                                    error={fieldErrorMessage('resolution_notification_date')}
                                />
                            </div>

                            <div className="col-md-4">
                                <InputField
                                    label="Días hábiles para pago"
                                    type="number"
                                    value={form.days_remaining}
                                    onChange={(value) => handleInputChange('days_remaining', value)}
                                    onFocus={() => onFocusRemove('days_remaining')}
                                    error={fieldErrorMessage('days_remaining')}
                                    placeholder="60"
                                />
                                <small className="text-muted">Días hábiles (excluye fines de semana y feriados)</small>
                            </div>

                            <div className="col-md-4">
                                <InputField
                                    label="Fecha límite de pago"
                                    type="date"
                                    value={paymentDeadlineDate ? convertFormattedDateToISO(paymentDeadlineDate) : ''}
                                    onChange={() => undefined}
                                    disabled={true}
                                />
                            </div>

                            <div className="col-12">
                                <h6>Certificado de marca</h6>
                                <FileUpload
                                    onFileSelect={(file) => setCertificateFile(file)}
                                    currentFile={certificateFile}
                                    existingFileUrl={resolutionData.certificate_file}
                                    existingFileName="Certificado de marca"
                                    acceptedTypes=".pdf"
                                    maxSize={10}
                                />
                                <small className="text-muted">Adjunte el certificado de marca. El archivo debe pesar máximo 10MB.</small>
                            </div>

                            <div className="col-12">
                                <TextAreaField
                                    label="Observaciones"
                                    value={form.observations}
                                    onChange={(value) => handleInputChange('observations', value)}
                                    onFocus={() => onFocusRemove('observations')}
                                    error={fieldErrorMessage('observations')}
                                    rows={4}
                                    placeholder="Resolución definitiva de registro de marca se encuentra cargado en nuestra plataforma."
                                />
                            </div>
                        </div>

                        <div className="d-flex justify-content-end mt-4">
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                title="Guardar"
                                onClick={handleUpdateResolution}
                                loading={fetchingUpdateDefinitiveResolution}
                            />
                        </div>

                        <Alert variant="info" className="mt-3">
                            Cliente será notificado por correo y en Monere.
                        </Alert>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DefinitiveResolutionStep;

